// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { useState, useEffect } from "react";

import { generateClient } from "@aws-amplify/api";

import { AwsAccountCost } from "../../../types/usageCostAllocation";
import { awsAccountCostGet } from "../../../graphql/usageCostAllocation/queries";

const client = generateClient({ authMode: "userPool" });

export interface UseAwsAccountCostResult {
	costAmount: number | null;
	currency: string;
	loading: boolean;
	error: string | null;
}

export function useAwsAccountCost(
	billingPeriod: string,
	enabled: boolean
): UseAwsAccountCostResult {
	const [costAmount, setCostAmount] = useState<number | null>(null);
	const [currency, setCurrency] = useState<string>("USD");
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!enabled) {
			setCostAmount(null);
			setCurrency("USD");
			setLoading(false);
			setError(null);
			return;
		}

		async function fetchCost() {
			setLoading(true);
			setError(null);
			try {
				const response = await client.graphql({
					query: awsAccountCostGet,
					variables: { billingPeriod },
				});
				const data: AwsAccountCost = (response as any).data
					.awsAccountCostGet;
				setCostAmount(data.costAmount);
				setCurrency(data.currency);
			} catch (err: any) {
				console.error("Failed to fetch AWS account cost", err);
				setError(
					err?.message ?? "Failed to fetch AWS account cost"
				);
			} finally {
				setLoading(false);
			}
		}
		fetchCost();
	}, [billingPeriod, enabled]);

	return { costAmount, currency, loading, error };
}
